import React, { useEffect, useMemo } from 'react';

import { Flex, Input } from '@opendoor/bricks/core';

import { ContactDetail } from 'buying/stateMachines/buyDirectCheckoutFlowStateMachine';

interface IContactFormProps {
  contactDetails?: Record<string, ContactDetail>;
  index: number;
  onFormChange: (key: string, field: string, value: string | number | boolean) => void;
}

/**
 * Contact Form
 * storybook-check-ignore
 */
export const ContactForm: React.FC<IContactFormProps> = ({
  contactDetails,
  index,
  onFormChange,
}) => {
  // Handle input focus by priority based on pre-filled data or lack thereof
  // useRef doesn't work. I don't think they are passed through as props on Bricks <Input>
  // Using vanilla JS to focus on the input element
  useEffect(() => {
    if (!contactDetails?.[index]?.name) {
      return document.getElementById(`input-first-name-${index}`)?.focus();
    }
    if (!contactDetails?.[index]?.phone) {
      return document.getElementById(`input-phone-number-${index}`)?.focus();
    }
    if (!contactDetails?.[index]?.email) {
      return document.getElementById(`input-email-${index}`)?.focus();
    }
    if (!contactDetails?.[index]?.emailVerification) {
      return document.getElementById(`input-email-verify-${index}`)?.focus();
    }
  }, [contactDetails, index]);

  const handleOnFormChange = (event: React.ChangeEvent<HTMLInputElement> | undefined) => {
    const contactid = event?.target?.dataset?.contactid;
    const field = event?.target?.dataset?.field;
    const value = event?.target?.value;
    if (contactid && field && value) {
      onFormChange(contactid, field, value);
    }
  };

  const isCoBuyer = useMemo(() => index === 1, [index]);

  return (
    <Flex
      flexDirection="column"
      alignItems="flex-start"
      justifyContent="flex-start"
      width="100%"
      mb={8}
    >
      <Flex
        flexDirection={['column', 'column', 'row', 'row']}
        justifyContent="space-between"
        width={['100%', '100%', '50%', '100%']}
        mt={4}
      >
        <Flex width="100%" flexDirection="row">
          <Input
            analyticsName="input-first-name"
            id={`input-first-name-${index}`}
            data-contactid={index}
            data-field="name"
            type="text"
            label={isCoBuyer ? 'Co-buyer’s name' : 'Name'}
            invalid={false}
            disabled={false}
            required={true}
            orientation="vertical"
            placeholder="First and last name"
            onChange={handleOnFormChange}
            defaultValue={contactDetails?.[index]?.name}
            mb={[7, null, null]}
            width={['100%', '100%', '98%', '98%']}
          />
        </Flex>
        <Flex width="100%" flexDirection="row" justifyContent="end">
          <Input
            analyticsName="input-phone-number"
            id={`input-phone-number-${index}`}
            type="tel"
            data-contactid={index}
            data-field="phone"
            label={isCoBuyer ? 'Co-buyer’s phone number' : 'Phone number'}
            invalid={false}
            disabled={false}
            required={true}
            orientation="vertical"
            placeholder="123-555-3226"
            value={contactDetails?.[index]?.phone}
            onChange={handleOnFormChange}
            mb={[7, null, null]}
            width={['100%', '100%', '98%', '98%']}
          />
        </Flex>
      </Flex>
      <Flex
        flexDirection={['column', 'column', 'row', 'row']}
        justifyContent="space-between"
        width={['100%', '100%', '50%', '100%']}
        mt={[null, null, 4]}
      >
        <Flex width="100%" flexDirection="row">
          <Input
            analyticsName="input-email"
            id={`input-email-${index}`}
            type="email"
            data-contactid={index}
            data-field="email"
            label={isCoBuyer ? 'Co-buyer’s email' : 'Email'}
            disabled={!isCoBuyer}
            required={true}
            orientation="vertical"
            placeholder="email@email.com"
            onChange={handleOnFormChange}
            defaultValue={contactDetails?.[index]?.email}
            mb={[7, null, null]}
            width={['100%', '100%', '98%', '98%']}
          />
        </Flex>
        <Flex width="100%" flexDirection="row" justifyContent="end">
          <Input
            analyticsName="input-email-verify"
            id={`input-email-verify-${index}`}
            type="email"
            data-contactid={index}
            data-field="emailVerification"
            label={isCoBuyer ? 'Verify co-buyer’s email' : 'Verify email'}
            disabled={!isCoBuyer}
            required={true}
            orientation="vertical"
            placeholder="email@email.com"
            defaultValue={contactDetails?.[index]?.emailVerification}
            onChange={handleOnFormChange}
            width={['100%', '100%', '98%', '98%']}
          />
        </Flex>
      </Flex>
    </Flex>
  );
};
